import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { config } from '@config/index';
import { APIResponse, APIError } from '@types/index';
import { handleAPIError, getErrorMessage, APIError as APIErrorClass } from './error-handler';
import { transformResponseKeys } from './response-transformer';
import { authService, isSessionRevoked } from '../auth';
import { IMPERSONATION_RETURN_PATH, restoreOriginSession } from '../auth/impersonation';

/**
 * An admin "logged in as" a user: when that session can't continue (its 60-minute limit, or any auth
 * failure), put the admin's OWN session back instead of logging them out. Returns true when it did.
 */
const endImpersonationIfAny = (): boolean => {
  if (!restoreOriginSession()) return false;
  try { sessionStorage.setItem('impersonationEnded', '1'); } catch { /* storage unavailable */ }
  window.location.href = IMPERSONATION_RETURN_PATH;
  return true;
};

// Auth endpoints must never trigger a refresh-and-retry themselves — refreshing
// on a failed login makes no sense, and retrying the refresh call on its own
// 401 would loop forever.
const AUTH_ENDPOINT_MARKERS = ['/Auth/connect-token', '/Auth/refresh-token', '/Auth/token', '/Auth/connect'];

class APIClient {
  private axiosInstance: AxiosInstance;
  // Coalesces concurrent 401s into a single refresh call instead of firing one
  // refresh request per failed request.
  private refreshPromise: Promise<void> | null = null;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL: config.api.baseURL,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (requestConfig) => {
        // Add auth token if available. Stored token may already include 'Bearer ' prefix.
        const token = localStorage.getItem('authToken');
        // Debug logging to help ensure token is attached
        if (import.meta.env.DEV) {
          console.debug('[API Client] Outgoing request', requestConfig.method, requestConfig.url, 'tokenPresent=', !!token);
        }
        if (token) {
          requestConfig.headers.Authorization = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
        }
        return requestConfig;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config as (AxiosRequestConfig & { _retry?: boolean }) | undefined;
        const isAuthEndpoint = AUTH_ENDPOINT_MARKERS.some((m) => originalRequest?.url?.includes(m));

        // Single-device rule: this session was ended (signed in elsewhere / logged out). Refreshing can't
        // help -- clear local state and explain on the login page instead of a silent bounce.
        if (error.response?.status === 401 && error.response?.data?.code === 'IMPERSONATION_ENDED' && endImpersonationIfAny()) {
          return Promise.reject(error);
        }
        if (error.response?.status === 401 && isSessionRevoked(error)) {
          try { sessionStorage.setItem('sessionRevoked', '1'); } catch { /* storage unavailable */ }
          localStorage.removeItem('authToken');
          localStorage.removeItem('aibuddy_user');
          window.location.href = '/auth/login'; // the real login route ('/login' only lands on the marketing page)
          return Promise.reject(error);
        }

        // A token that merely EXPIRED should never interrupt the user: refresh
        // it silently and replay the exact same request once, transparently.
        if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthEndpoint) {
          originalRequest._retry = true;
          try {
            await this.refreshTokenOnce();
            const newToken = localStorage.getItem('authToken');
            if (newToken) {
              originalRequest.headers = originalRequest.headers || {};
              (originalRequest.headers as any).Authorization = newToken.startsWith('Bearer ') ? newToken : `Bearer ${newToken}`;
            }
            return this.axiosInstance(originalRequest);
          } catch (refreshError) {
            console.warn('[API Client] Token refresh failed - logging out', refreshError);
            if (endImpersonationIfAny()) return Promise.reject(error);
            if (isSessionRevoked(refreshError)) {
              try { sessionStorage.setItem('sessionRevoked', '1'); } catch { /* storage unavailable */ }
            }
            localStorage.removeItem('authToken');
            localStorage.removeItem('aibuddy_user');
            window.location.href = isSessionRevoked(refreshError) ? '/auth/login' : '/login';
            return Promise.reject(error);
          }
        }

        // Plan gating: the backend refused a feature outside the user's plan. Announce it so the
        // EntitlementProvider can show the upgrade prompt, wherever in the app the call came from.
        const detail = error.response?.data?.detail;
        if (error.response?.status === 403 && detail && typeof detail === 'object' && (detail.code === 'FEATURE_NOT_IN_PLAN' || detail.code === 'FEATURE_COMING_SOON')) {
          window.dispatchEvent(new CustomEvent('feature-locked', { detail }));
        }

        // Refresh already attempted (and the retried call is STILL 401), or this
        // 401 came from an auth endpoint itself — no more silent recovery.
        if (error.response?.status === 401) {
          console.warn('[API Client] Unauthorized access - redirecting to login');
          if (endImpersonationIfAny()) return Promise.reject(error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('aibuddy_user');
          window.location.href = '/login';
        }

        // Always reject to let caller handle the error
        return Promise.reject(error);
      }
    );
  }

  /** Coalesces concurrent callers onto a single in-flight refresh call. */
  private refreshTokenOnce(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = authService.refreshToken().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.axiosInstance.get<T>(url, config);
      return {
        status: 'success',
        data: transformResponseKeys(response.data),
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.axiosInstance.post<T>(url, data, config);
      return {
        status: 'success',
        data: transformResponseKeys(response.data),
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.axiosInstance.put<T>(url, data, config);
      return {
        status: 'success',
        data: transformResponseKeys(response.data),
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.axiosInstance.patch<T>(url, data, config);
      return {
        status: 'success',
        data: transformResponseKeys(response.data),
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<APIResponse<T>> {
    try {
      const response = await this.axiosInstance.delete<T>(url, config);
      return {
        status: 'success',
        data: transformResponseKeys(response.data),
      };
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * Handle errors using centralized error handler
   * Throws APIError so that calling code can catch it
   */
  private handleError(error: any): never {
    try {
      // Try to use the centralized error handler
      handleAPIError(error);
    } catch (apiError) {
      // Re-throw so calling code receives the error
      throw apiError;
    }

    // Fallback - this should never be reached due to handleAPIError always throwing
    throw new Error('An unknown error occurred');
  }
}

export const apiClient = new APIClient();
